import { SITE_FACTS } from "@shared/siteFacts";
import { Link } from "wouter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { visibleCategories } from "@/lib/tools-data";
import { BackToHomeLink } from "@/components/BackToHomeLink";

export default function About() {
  const slidesToolCount = visibleCategories.find((c) => c.id === "powerpoint-slides")?.tools.length ?? 0;
  usePageMeta({
    title: "About",
    description:
      `${SITE_FACTS.toolCountLabel} professional tools, premium applications and workflow products to speed up your work! FreeDailyPro is a browser-based hub built to end the site-hopping, logins, and cost of getting everyday tasks done.`,
    path: "/about",
  });

  return (
    <div className="container max-w-3xl mx-auto py-12 px-4">
      <BackToHomeLink />
      <h1 className="text-3xl font-bold mb-2">About FreeDailyPro</h1>
      <p className="text-lg text-primary font-medium mb-8">
        {SITE_FACTS.toolCountPhrase} to speed up your work!
      </p>

      <div className="space-y-6 text-sm leading-relaxed text-foreground/90">
        <h2 className="text-lg font-semibold pt-1">
          My Journey Building FreeDailyPro
        </h2>

        <p>
          Individuals and professionals constantly struggle to find the right
          tools to get their daily tasks done — editing a PDF, resizing an
          image, building a resume, refining a website with logos and colors.
          The tools you need change completely depending on the work you're
          doing, and finding the right one, all in one place, is genuinely hard.
          On top of that, you end up juggling apps, sites, logins, payments, and
          security concerns — attention that should go to the actual task
          instead gets diverted into an unnecessary logistical nightmare.
        </p>

        <p>
          So I decided to build a centralized hub, where anyone can come use
          most of the tools they need for daily personal and professional work —
          at no cost, with no login required for any tool, and with their data
          kept safe. It brings speed, productivity, and peace of mind, plus the
          ability to do more than you originally came looking for, once you
          discover the other tools sitting right alongside the one you needed.
        </p>

        <p>
          With that in mind, most tools run entirely in your browser — any
          device, any platform, anywhere in the world — and the few that need
          a server are labeled on their own page. To help professionals specifically, I also built a{" "}
          <Link href="/solutions" className="text-primary underline">
            catalog of tools organized by role
          </Link>
          , to make finding the right ones easier.
        </p>

        <p>
          Only once someone becomes a genuinely heavy user on a particular day
          do we ask for just $1 via a Day Pass to help cover the cost of running
          the service. For people who want an ad-free experience long-term,
          there's also a small $4.99/month subscription. Keeping both of those
          affordable, rather than charging what the market might bear, is
          deliberate — it's what lets more people actually use the site day to
          day. A bigger, happier user base is what funds newer, higher-end
          tools down the road, growing the business by staying competitive and
          welcoming rather than squeezing a smaller group of users harder.
        </p>

        <p>
          I hope people find it genuinely useful, and this is very much still
          growing — More specialized tools are on the roadmap as we grow.
        </p>

        <p className="text-xs text-muted-foreground">
          FreeDailyPro is operated by DreamUSA LLC, based in North Carolina,
          USA.
        </p>

        <h2 className="text-lg font-semibold pt-4">What's Included</h2>
        <p>
          FreeDailyPro spans {SITE_FACTS.toolCountPhrase} across{" "}
          {visibleCategories.length} categories. On the document side, there's a
          full{" "}
          <Link href="/category/pdf-tools" className="text-primary underline">
            PDF toolkit
          </Link>{" "}
          (merge, split, compress, convert to and from Word/Excel/JPG,
          watermark, sign, and more) alongside{" "}
          <Link href="/category/image-tools" className="text-primary underline">
            image tools
          </Link>{" "}
          for compressing, resizing, converting, and removing backgrounds, plus
          a growing set of{" "}
          <Link href="/category/video-tools" className="text-primary underline">
            video tools
          </Link>{" "}
          for trimming, compressing, and converting clips.
        </p>
        <p>
          For everyday writing and data tasks, there's a full{" "}
          <Link href="/category/text-tools" className="text-primary underline">
            text toolkit
          </Link>{" "}
          (word counting, case conversion, grammar checking, paraphrasing) and a{" "}
          <Link
            href="/category/security-tools"
            className="text-primary underline"
          >
            developer toolkit
          </Link>{" "}
          (JSON formatting, regex testing, hashing, JWT decoding, diff
          checking).
        </p>
        <p>
          On the numbers side, FreeDailyPro includes dedicated{" "}
          <Link
            href="/category/tax-calculators"
            className="text-primary underline"
          >
            tax calculators
          </Link>{" "}
          for the US, UK, and India (sales tax, VAT, GST, income tax, and
          self-employment tax),{" "}
          <Link
            href="/category/finance-calculators"
            className="text-primary underline"
          >
            finance and retirement calculators
          </Link>{" "}
          (loans, mortgages, Social Security, 401(k), RMD, and FIRE planning),
          and{" "}
          <Link
            href="/category/health-calculators"
            className="text-primary underline"
          >
            health calculators
          </Link>{" "}
          (BMI, body fat, BMR, calorie needs, ideal weight, pregnancy due dates,
          and water intake).
        </p>
        <p>
          For business and legal paperwork, there's a{" "}
          <Link
            href="/category/business-tools"
            className="text-primary underline"
          >
            business toolkit
          </Link>{" "}
          covering resumes, invoices, receipts, expense tracking, and general
          document templates (NDAs, lease agreements, power of attorney, bills
          of sale, promissory notes, employment offer letters, and eviction
          notices), plus direct links to official US government tax and
          employment forms (W-4, W-9, 1099-NEC, and I-9).
        </p>
        <p>
          Rounding it out: a full{" "}
          <Link
            href="/category/design-tools"
            className="text-primary underline"
          >
            design toolkit
          </Link>{" "}
          (color palettes, gradients, favicons, logos, mockups),{" "}
          <Link href="/category/qr-tools" className="text-primary underline">
            QR and barcode tools
          </Link>
          ,{" "}
          <Link href="/category/seo-tools" className="text-primary underline">
            SEO tools
          </Link>{" "}
          (meta tags, keyword density, sitemaps), and general{" "}
          <Link href="/category/file-tools" className="text-primary underline">
            file utilities
          </Link>{" "}
          (ZIP, audio conversion, and OCR).
        </p>
        <p>
          Newer additions include a{" "}
          <Link href="/category/powerpoint-slides" className="text-primary underline">
            PowerPoint and Slides category
          </Link>{" "}
          with {slidesToolCount} tools (convert, merge, split, compress and clean decks, or build one from text),
          a Compress PDF tool with four modes (Balanced, Aggressive, Extreme and a target file size),{" "}
          <Link href="/category/event-tools" className="text-primary underline">
            event planning tools
          </Link>
          , book-publishing and legal-document helpers, and a first payment date on the mortgage, loan and student loan
          calculators.
        </p>
        <p>
          For developers and AI agents, the{" "}
          <Link href="/developers" className="text-primary underline">
            API and MCP server
          </Link>{" "}
          offers {SITE_FACTS.apiCount} REST endpoints and {SITE_FACTS.mcpCount} MCP tools, including{" "}
          {SITE_FACTS.pptxApiCount} for PowerPoint, behind one API key.
        </p>
        <p>
          More than {SITE_FACTS.usage.users.replace("+", "")} people use FreeDailyPro, from {SITE_FACTS.usage.countries} countries.
        </p>

        <p>
          Have a suggestion for a new tool, or found something that isn't
          working right? I'd love to hear from you — see the{" "}
          <Link href="/contact" className="text-primary underline">
            Contact page
          </Link>
          . You can also read more on the{" "}
          <Link href="/blog" className="text-primary underline">
            blog
          </Link>
          , where I write practical guides for getting the most out of these
          tools.
        </p>
      </div>
    </div>
  );
}
